import { useState, useEffect, useCallback } from "react";
import { Calendar, MapPin, Clock, ArrowRight, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pastEvents = [
  {
    id: "bridge-lodge-open-day",
    titleKey: "eventTitle",
    date: "June 20th, 2026",
    time: "12:00 noon - 4:00 PM",
    location: "Village Square, Abule Imole",
    descKey: "eventDesc",
    image: "https://customer-assets.emergentagent.com/job_abule-imole/artifacts/x2lwzvgp_WhatsApp%20Image%202026-04-17%20at%2000.10.23.jpeg",
  },
];

const upcomingEvents = [];

function getYoutubeEmbedUrl(url) {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

function getNarrative(content, lang) {
  if (lang === "fr" && content.narrative_fr) return content.narrative_fr;
  if (lang === "de" && content.narrative_de) return content.narrative_de;
  return content.narrative;
}

function hasContent(content) {
  if (!content) return false;
  return Boolean(content.narrative || content.photos?.length || content.youtube_urls?.length);
}

const CAROUSEL_ARROW_SIZE = 22;
const CAROUSEL_INTERVAL_MS = 5000;

/* --- Carousel --- */

function CarouselControls({ onPrev, onNext, current, total }) {
  return (
    <>
      <button onClick={onPrev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/80 hover:bg-white text-[#1C2333] flex items-center justify-center transition-all shadow-sm">
        <ChevronLeft size={CAROUSEL_ARROW_SIZE} />
      </button>
      <button onClick={onNext} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/80 hover:bg-white text-[#1C2333] flex items-center justify-center transition-all shadow-sm">
        <ChevronRight size={CAROUSEL_ARROW_SIZE} />
      </button>
      <div className="absolute bottom-3 right-3 bg-[#1C2333]/60 text-white text-sm px-3 py-1">
        {current + 1} / {total}
      </div>
    </>
  );
}

function CarouselThumbnails({ photos, current, onSelect }) {
  return (
    <div className="flex gap-2 mt-3 overflow-x-auto py-1">
      {photos.map((photo, idx) => (
        <button key={photo.id} onClick={() => onSelect(idx)}
          className={`shrink-0 w-16 h-16 sm:w-20 sm:h-20 overflow-hidden transition-all duration-200 ${idx === current ? "ring-2 ring-[#C4883A] opacity-100" : "opacity-50 hover:opacity-80"}`}>
          <img src={`${API}/files/${photo.storage_path}`} alt={photo.original_filename} className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
  );
}

function PhotoCarousel({ photos }) {
  const [current, setCurrent] = useState(0);
  const total = photos.length;

  const prev = useCallback(() => setCurrent((c) => (c === 0 ? total - 1 : c - 1)), [total]); // eslint-disable-line react-hooks/exhaustive-deps
  const next = useCallback(() => setCurrent((c) => (c === total - 1 ? 0 : c + 1)), [total]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setInterval(next, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [next]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mb-8">
      <div className="relative overflow-hidden bg-[#1C2333]/5">
        <div className="relative w-full h-[300px] sm:h-[450px] lg:h-[550px]">
          <img src={`${API}/files/${photos[current].storage_path}`} alt={photos[current].original_filename} className="w-full h-full object-contain transition-opacity duration-500" />
          <CarouselControls onPrev={prev} onNext={next} current={current} total={total} />
        </div>
      </div>
      <CarouselThumbnails photos={photos} current={current} onSelect={setCurrent} />
    </div>
  );
}

/* --- Expanded Content --- */

function NarrativeBlock({ text }) {
  return (
    <div className="mb-8">
      <p className="text-base sm:text-lg leading-relaxed text-[#1C2333]/80 whitespace-pre-line">{text}</p>
    </div>
  );
}

function YoutubeVideos({ urls }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {urls.map((url, idx) => {
        const embedUrl = getYoutubeEmbedUrl(url);
        if (!embedUrl) return null;
        return (
          <div key={`yt-${idx}`} className="relative aspect-video">
            <iframe src={embedUrl} title={`Video ${idx + 1}`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        );
      })}
    </div>
  );
}

function ExpandedContent({ eventId }) {
  const { lang, t } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/events/${eventId}/content`)
      .then((res) => setContent(res.data))
      .catch(() => setContent(null))
      .finally(() => setLoading(false));
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="py-8 text-center text-[#1C2333]/40">{t.events.loading}</div>;
  if (!hasContent(content)) return <div className="py-8 text-center text-[#1C2333]/40 text-lg">{t.events.noContent}</div>;

  const narrative = getNarrative(content, lang);

  return (
    <div className="pt-8 border-t border-[#C8C5BC]/30">
      {narrative && <NarrativeBlock text={narrative} />}
      {content.photos?.length > 0 && <PhotoCarousel photos={content.photos} />}
      {content.youtube_urls?.length > 0 && <YoutubeVideos urls={content.youtube_urls} />}
    </div>
  );
}

/* --- Event Card --- */

function EventCard({ event, isReversed, isPast }) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);

  return (
    <div data-testid={`event-card-${event.id}`} className="overflow-hidden bg-white border border-[#C8C5BC]/30 shadow-sm hover:shadow-lg transition-all duration-500">
      <div className={`group flex flex-col ${isReversed ? "md:flex-row-reverse" : "md:flex-row"} items-stretch gap-0`}>
        <div className="md:w-1/2 h-64 md:h-80 overflow-hidden relative">
          <img src={event.image} alt={t.events[event.titleKey]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-[#1C2333]/30 to-transparent" />
        </div>
        <div className="md:w-1/2 p-10 sm:p-12 lg:p-16 flex flex-col justify-center">
          <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-semibold text-[#1C2333] mb-4">{t.events[event.titleKey]}</h3>
          <div className="flex flex-col gap-2 mb-6">
            <div className="flex items-center gap-2 text-[#1B3D2F]"><Calendar size={16} className="text-[#C4883A]" /><span className="text-sm sm:text-base">{event.date}</span></div>
            <div className="flex items-center gap-2 text-[#1B3D2F]"><Clock size={16} className="text-[#C4883A]" /><span className="text-sm sm:text-base">{event.time}</span></div>
            <div className="flex items-center gap-2 text-[#1B3D2F]"><MapPin size={16} className="text-[#C4883A]" /><span className="text-sm sm:text-base">{event.location}</span></div>
          </div>
          <p className="text-base sm:text-lg leading-relaxed text-[#1C2333]/70">{t.events[event.descKey]}</p>
          {isPast && (
            <button onClick={() => setExpanded(!expanded)} data-testid={`expand-btn-${event.id}`}
              className="mt-6 inline-flex items-center gap-2 text-[#C4883A] hover:text-[#A8722E] font-medium transition-colors self-start">
              {expanded ? t.events.showLess : t.events.seeHowItWent}
              {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          )}
        </div>
      </div>
      {isPast && expanded && (
        <div className="px-10 sm:px-12 lg:px-16 pb-10">
          <ExpandedContent eventId={event.id} />
        </div>
      )}
    </div>
  );
}

/* --- Page Sections --- */

function EventsHero() {
  const { t } = useLanguage();
  return (
    <section data-testid="events-hero" className="bg-[#C8C5BC]/30 pt-28 sm:pt-48 pb-20 sm:pb-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-base sm:text-lg tracking-[0.2em] uppercase font-semibold text-[#C4883A] mb-4 animate-fade-in-up opacity-0">{t.events.label}</p>
        <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-[#1C2333] max-w-3xl mx-auto leading-tight animate-fade-in-up opacity-0 animate-delay-100">
          {t.events.heroTitle1}<br /><span className="font-medium text-[#1B3D2F]">{t.events.heroTitle2}</span>
        </h1>
        <p className="mt-8 text-[#1C2333]/70 text-lg sm:text-xl lg:text-2xl max-w-2xl mx-auto leading-relaxed animate-fade-in-up opacity-0 animate-delay-200">{t.events.heroBody}</p>
      </div>
    </section>
  );
}

function EventsTabs() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("upcoming");
  const currentEvents = activeTab === "upcoming" ? upcomingEvents : pastEvents;
  const emptyMessage = activeTab === "upcoming" ? t.events.noUpcoming : t.events.noPast;

  return (
    <section data-testid="events-list" className="py-20 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div data-testid="events-tabs" className="flex justify-center gap-2 mb-16">
          <button data-testid="tab-upcoming" onClick={() => setActiveTab("upcoming")} className={`px-8 py-3 font-['Cormorant_Garamond'] text-lg sm:text-xl font-semibold tracking-wide transition-all duration-300 ${activeTab === "upcoming" ? "bg-[#1B3D2F] text-white" : "border border-[#C8C5BC] text-[#1C2333]/60 hover:text-[#1C2333] hover:border-[#1C2333]/40"}`}>
            {t.events.upcomingTab}
          </button>
          <button data-testid="tab-past" onClick={() => setActiveTab("past")} className={`px-8 py-3 font-['Cormorant_Garamond'] text-lg sm:text-xl font-semibold tracking-wide transition-all duration-300 ${activeTab === "past" ? "bg-[#1B3D2F] text-white" : "border border-[#C8C5BC] text-[#1C2333]/60 hover:text-[#1C2333] hover:border-[#1C2333]/40"}`}>
            {t.events.pastTab}
          </button>
        </div>
        {currentEvents.length > 0 ? (
          <div className="flex flex-col gap-16 lg:gap-24">
            {currentEvents.map((event, idx) => (
              <EventCard key={event.id} event={event} isReversed={idx % 2 !== 0} isPast={activeTab === "past"} />
            ))}
          </div>
        ) : (
          <div data-testid="events-empty" className="text-center py-16">
            <p className="text-lg sm:text-xl text-[#1C2333]/50 font-['Cormorant_Garamond']">{emptyMessage}</p>
          </div>
        )}
      </div>
    </section>
  );
}

function EventsCta() {
  const { t } = useLanguage();
  return (
    <section data-testid="events-cta" className="bg-[#D0D5C8] py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium tracking-tight text-[#1C2333] mb-6">{t.events.ctaTitle}</h2>
        <p className="text-lg sm:text-xl leading-relaxed text-[#1C2333]/70 max-w-2xl mx-auto mb-10">{t.events.ctaBody}</p>
        <a href="/contact" data-testid="events-cta-contact" className="bg-[#C4883A] text-white hover:bg-[#A8722E] px-10 py-5 text-lg sm:text-xl font-medium transition-all duration-300 inline-flex items-center gap-2">
          {t.events.ctaBtn} <ArrowRight size={20} />
        </a>
      </div>
    </section>
  );
}

export default function Events() {
  return (
    <div data-testid="events-page">
      <EventsHero />
      <EventsTabs />
      <EventsCta />
    </div>
  );
}
